import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { isTourDone, TOUR_COMPLETE_EVENT } from './ProductTour';

const STORAGE_KEY = 'ch_live_coding_promo_seen_at';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 1600;

const features = [
  {
    icon: <CodeOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Shared editor',
    body: 'Type together in real time with cursor presence.',
  },
  {
    icon: <VideocamOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Live video',
    body: 'Face-to-face while you debug and interview.',
  },
  {
    icon: <BoltOutlinedIcon sx={{ fontSize: 20 }} />,
    title: 'Run & share output',
    body: 'Compile once — both see who ran the code.',
  },
];

const shouldShowPromo = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    return Date.now() - last >= WEEK_MS;
  } catch {
    return false;
  }
};

const markPromoSeen = () => {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
};

/**
 * First-load / weekly promo for Live Coding Room.
 * Skips immersive routes (already in a room).
 */
const LiveCodingPromoModal = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isImmersive = location.pathname.startsWith('/live-coding');

  useEffect(() => {
    if (isImmersive) {
      setOpen(false);
      return undefined;
    }
    if (!shouldShowPromo()) return undefined;

    // Wait until first-time product tour is finished
    if (!isTourDone()) {
      const onTourDone = () => {
        if (!shouldShowPromo()) return;
        setTimeout(() => setOpen(true), 800);
      };
      window.addEventListener(TOUR_COMPLETE_EVENT, onTourDone);
      return () => window.removeEventListener(TOUR_COMPLETE_EVENT, onTourDone);
    }

    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isImmersive, location.pathname]);

  const handleClose = () => {
    markPromoSeen();
    setOpen(false);
  };

  const handleTry = () => {
    markPromoSeen();
    setOpen(false);
    navigate('/live-coding');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: isDark ? '#161b22' : '#fff',
          backgroundImage: 'none',
          boxShadow: isDark
            ? '0 24px 64px rgba(0,0,0,0.55)'
            : '0 24px 64px rgba(15, 60, 70, 0.18)',
          border: isDark ? '1px solid #30363d' : '1px solid rgba(19,114,116,0.12)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(8, 12, 18, 0.55)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Hero band */}
        <Box
          sx={{
            position: 'relative',
            px: { xs: 2.5, sm: 3.5 },
            pt: { xs: 3, sm: 3.5 },
            pb: { xs: 2.5, sm: 3 },
            background: isDark
              ? 'linear-gradient(135deg, #1a2a2e 0%, #162028 45%, #1f1520 100%)'
              : 'linear-gradient(135deg, #e8f6f6 0%, #f7fbfc 50%, #fff5f8 100%)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 220,
              height: 220,
              borderRadius: '50%',
              right: -60,
              top: -80,
              background: isDark
                ? 'radial-gradient(circle, rgba(245,0,87,0.22) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(245,0,87,0.14) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              left: -50,
              bottom: -70,
              background: isDark
                ? 'radial-gradient(circle, rgba(19,114,116,0.28) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(19,114,116,0.16) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <IconButton
            aria-label="Close"
            onClick={handleClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: isDark ? '#8b949e' : '#6b7780',
              zIndex: 2,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.4,
              mb: 1.75,
              borderRadius: 10,
              bgcolor: isDark ? 'rgba(245,0,87,0.18)' : 'rgba(245,0,87,0.1)',
              color: '#f50057',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 14 }} />
            New · Pair programming
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              fontSize: { xs: '1.55rem', sm: '1.85rem' },
              lineHeight: 1.2,
              mb: 1,
              position: 'relative',
            }}
          >
            Code together,{' '}
            <Box component="span" sx={{ color: '#137274' }}>
              live
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: isDark ? '#aab2bb' : '#5f6b73',
              maxWidth: 420,
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              lineHeight: 1.55,
              position: 'relative',
            }}
          >
            Spin up a private Live Coding Room — shared editor, video, and run
            output. Perfect for interviews, mentoring, and study sessions.
          </Typography>
        </Box>

        {/* Features */}
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 2.5, pb: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
            }}
          >
            {features.map((f) => (
              <Box
                key={f.title}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: isDark ? '1px solid #30363d' : '1px solid rgba(15,60,70,0.1)',
                  bgcolor: isDark ? '#0d1117' : '#f7fafb',
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    bgcolor: isDark ? 'rgba(19,114,116,0.22)' : 'rgba(19,114,116,0.1)',
                    color: '#137274',
                  }}
                >
                  {f.icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.35 }}>
                  {f.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#8b949e' : '#5f6b73',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                  }}
                >
                  {f.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: 2,
            pb: { xs: 2.5, sm: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isDark ? '#6e7681' : '#8a949c',
              textAlign: { xs: 'center', sm: 'left' },
              flexShrink: 1,
            }}
          >
            We&apos;ll remind you again in a week if you skip.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: isDark ? '#8b949e' : '#5f6b73',
                whiteSpace: 'nowrap',
              }}
            >
              Maybe later
            </Button>
            <Button
              variant="contained"
              onClick={handleTry}
              startIcon={<VideocamOutlinedIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                bgcolor: '#f50057',
                boxShadow: '0 6px 16px rgba(245, 0, 87, 0.35)',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#c51162' },
              }}
            >
              Try Live Coding Room
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LiveCodingPromoModal;
